"use client";

import React, { useEffect, useState } from "react";
import { Check, ChevronDown, Link2 } from "lucide-react";

export interface TocEntry {
  id: string;
  label: string;
}

interface ArticleSidebarProps {
  /** "rail" is the tinted left column on lg+; "bar" is the sticky "On this page" bar below lg. */
  variant: "rail" | "bar";
  items: TocEntry[];
  articleId: string;
  shareUrl: string;
  shareTitle: string;
}

function useActiveSection(ids: string[]) {
  const [activeId, setActiveId] = useState(ids[0] ?? "");

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-100px 0px -66% 0px", threshold: 0 }
    );
    ids.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [ids]);

  return activeId;
}

function useReadingProgress(articleId: string) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const el = document.getElementById(articleId);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const next = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 1;
      setProgress(Math.round(next * 100));
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [articleId]);

  return progress;
}

// Below lg the fixed site header (68px, 90px from md) plus the sticky "On this page" bar sit above the content.
function scrollOffset() {
  if (window.innerWidth >= 1024) return 100;
  return window.innerWidth >= 768 ? 160 : 136;
}

function scrollToSection(e: React.MouseEvent<HTMLAnchorElement>, id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  e.preventDefault();
  const top = el.getBoundingClientRect().top + window.scrollY - scrollOffset();
  window.scrollTo({ top, behavior: "smooth" });
  window.history.replaceState(null, "", `#${id}`);
}

function TocList({
  items,
  activeId,
  onNavigate,
}: {
  items: TocEntry[];
  activeId: string;
  onNavigate?: () => void;
}) {
  return (
    <ol className="space-y-0.5">
      {items.map(item => {
        const isActive = item.id === activeId;
        return (
          <li key={item.id}>
            <a
              href={`#${item.id}`}
              aria-current={isActive ? "location" : undefined}
              onClick={e => {
                scrollToSection(e, item.id);
                onNavigate?.();
              }}
              className={`group flex items-start gap-3 rounded-lg border-l-2 px-3 py-2 text-sm leading-snug transition-colors ${
                isActive
                  ? "border-primary bg-[#EEF3FD] font-medium text-primary"
                  : "border-transparent text-text-secondary hover:text-primary"
              }`}
            >
              <span
                aria-hidden="true"
                className={`mt-[7px] h-2 w-2 shrink-0 rounded-full transition-colors ${
                  isActive ? "bg-primary" : "bg-gray-300 group-hover:bg-primary/60"
                }`}
              />
              <span>{item.label}</span>
            </a>
          </li>
        );
      })}
    </ol>
  );
}

function ReadingProgress({ progress }: { progress: number }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between text-xs text-text-secondary">
        <span>Reading progress</span>
        <span className="tabular-nums">{progress}%</span>
      </div>
      <div
        role="progressbar"
        aria-label="Reading progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
        className="h-1 w-full overflow-hidden rounded-full bg-[#DDE3F3]"
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

const shareLinkClass =
  "inline-flex h-10 w-10 items-center justify-center rounded-lg border border-[#DDE3F3] bg-white text-text-primary transition-colors hover:border-primary hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary";

function ShareIcons({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = useState(false);
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked (insecure context, permissions); the other share links still work.
    }
  };

  return (
    <div>
      <p className="mb-2.5 text-sm font-medium text-text-primary">Share this article</p>
      <div className="flex items-center gap-2.5">
        <a
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on LinkedIn"
          className={shareLinkClass}
        >
          <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="currentColor" aria-hidden="true">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
          </svg>
        </a>
        <a
          href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on X"
          className={shareLinkClass}
        >
          <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="currentColor" aria-hidden="true">
            <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.932zm-1.29 19.494h2.039L6.486 3.24H4.298l13.313 17.407z" />
          </svg>
        </a>
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on Facebook"
          className={shareLinkClass}
        >
          <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="currentColor" aria-hidden="true">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
        </a>
        <button
          type="button"
          onClick={copyLink}
          aria-label={copied ? "Link copied" : "Copy link"}
          className={shareLinkClass}
        >
          {copied ? <Check className="h-[18px] w-[18px]" aria-hidden="true" /> : <Link2 className="h-[18px] w-[18px]" aria-hidden="true" />}
        </button>
      </div>
    </div>
  );
}

/**
 * Article navigation. "rail" is the left column on lg+ (contents, reading
 * progress, share). "bar" is the sticky "On this page" bar below lg: it shows the current section
 * and reading progress, with the contents list and share icons in an overlay dropdown.
 */
export function ArticleSidebar({ variant, items, articleId, shareUrl, shareTitle }: ArticleSidebarProps) {
  const ids = React.useMemo(() => items.map(item => item.id), [items]);
  const activeId = useActiveSection(ids);
  const progress = useReadingProgress(articleId);
  const [open, setOpen] = useState(false);
  const activeLabel = items.find(item => item.id === activeId)?.label;

  if (variant === "rail") {
    return (
      <div className="hidden lg:block">
        <aside aria-label="Article navigation" className="sticky top-28 space-y-6 py-8 pr-6">
          <nav aria-label="On this page">
            <p className="mb-3 px-3 text-xs font-bold uppercase tracking-wider text-text-primary">
              On this page
            </p>
            <TocList items={items} activeId={activeId} />
          </nav>
          <ReadingProgress progress={progress} />
          <div className="border-t border-[#E3E8F6] pt-5">
            <ShareIcons url={shareUrl} title={shareTitle} />
          </div>
        </aside>
      </div>
    );
  }

  return (
    <div className="sticky top-[calc(68px+var(--announcement-bar-height,0px))] z-30 mb-8 rounded-xl border border-gray-200 bg-white shadow-sm md:top-[calc(90px+var(--announcement-bar-height,0px))] lg:hidden">
      <button
        type="button"
        aria-expanded={open}
        aria-controls="mobile-toc"
        onClick={() => setOpen(prev => !prev)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm text-text-primary"
      >
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="shrink-0 font-semibold">On this page</span>
          {progress > 0 && activeLabel && (
            <span className="truncate text-xs text-text-secondary">{activeLabel}</span>
          )}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      <div
        role="progressbar"
        aria-label="Reading progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
        className="h-0.5 w-full overflow-hidden rounded-b-xl bg-gray-200"
      >
        <div
          className="h-full bg-primary transition-[width] duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>
      {/* Overlay, not in flow: opening or closing it must not shift the article under the reader. */}
      <div
        id="mobile-toc"
        hidden={!open}
        className="absolute inset-x-0 top-full mt-2 max-h-[calc(100vh-11rem)] overflow-y-auto rounded-xl border border-gray-200 bg-white px-2 py-2 shadow-lg"
      >
        <nav aria-label="On this page">
          <TocList items={items} activeId={activeId} onNavigate={() => setOpen(false)} />
        </nav>
        <div className="mt-3 border-t border-gray-200 px-3 pb-2 pt-3">
          <ShareIcons url={shareUrl} title={shareTitle} />
        </div>
      </div>
    </div>
  );
}
